import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/use-auth";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { Loader2, UserPlus, Trash2, KeyRound, Send } from "lucide-react";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { insertTeamMemberSchema, type TeamMember } from "@shared/schema";
import { z } from "zod";

// Schema pre pozvanie člena tímu
const inviteTeamMemberSchema = insertTeamMemberSchema.pick({
  invitedEmail: true,
  role: true,
  memberName: true,
  notes: true,
});

// Validácia hesla - musí obsahovať veľké písmeno, znak a číslo
const passwordValidation = z.string()
  .min(8, "Heslo musí mať aspoň 8 znakov")
  .regex(/[A-Z]/, "Heslo musí obsahovať aspoň jedno veľké písmeno")
  .regex(/[0-9]/, "Heslo musí obsahovať aspoň jedno číslo")
  .regex(/[^A-Za-z0-9]/, "Heslo musí obsahovať aspoň jeden špeciálny znak");

// Schema pre vytvorenie člena s účtom
const createTeamMemberSchema = inviteTeamMemberSchema.extend({
  password: passwordValidation,
});

// Schema pre resetovanie hesla
const resetPasswordSchema = z.object({
  newPassword: passwordValidation,
});

type InviteTeamMemberData = z.infer<typeof inviteTeamMemberSchema>;
type CreateTeamMemberData = z.infer<typeof createTeamMemberSchema>;
type ResetPasswordData = z.infer<typeof resetPasswordSchema>;

export default function TeamManagement() {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { data: billingData } = useQuery<{ plan?: { name?: string } }>({
    queryKey: ['/api/billing'],
    enabled: !!user,
    staleTime: 1000 * 60 * 5,
  });
  const [createWithAccount, setCreateWithAccount] = useState(false);
  const [resetPasswordMemberId, setResetPasswordMemberId] = useState<number | null>(null);
  const [editingMemberId, setEditingMemberId] = useState<number | null>(null);
  const [editingMemberName, setEditingMemberName] = useState("");
  const [editingMemberRole, setEditingMemberRole] = useState("");

  // Dynamicky nastavenie resolvera podľa createWithAccount
  const getResolver = () => {
    return zodResolver(createWithAccount ? createTeamMemberSchema : inviteTeamMemberSchema);
  };

  const form = useForm<CreateTeamMemberData>({
    resolver: getResolver(),
    defaultValues: {
      invitedEmail: "",
      role: "employee",
      memberName: "",
      notes: "",
      password: "",
    },
    mode: "onChange", // Enable validation on change
  });

  // Pri prepnutí režimu len vyčistiť chyby a prípadne heslo
  useEffect(() => {
    form.clearErrors();
    form.setValue("password", "");
  }, [createWithAccount]);

  const resetPasswordForm = useForm<ResetPasswordData>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: {
      newPassword: "",
    },
  });

  // Prístup k tímu: admin, superadmin, owner, beta_tester alebo plán Business/Freelancer
  const planName = (user?.plan || billingData?.plan?.name || '').toUpperCase();
  const hasTeamAccess = 
    user?.role === "admin" || user?.role === "superadmin" || user?.role === "owner" || user?.role === "beta_tester" ||
    planName === "BUSINESS" || planName === "FREELANCER" || planName === "PRO" ||
    (billingData?.plan?.name && /business|freelancer|pro/i.test(billingData.plan.name));

  // Načítaj členov tímu
  const { data: teamMembers = [], isLoading } = useQuery<TeamMember[]>({
    queryKey: ["/api/team-members"],
    enabled: !!user && hasTeamAccess,
    queryFn: async () => {
      const response = await apiRequest('GET', '/api/team-members');
      const data = await response.json();
      return data.items || data;
    }
  });

  // Mutácia pre pozvanie člena tímu
  const inviteTeamMemberMutation = useMutation({
    mutationFn: async (data: InviteTeamMemberData) => {
      // Backend očakáva kľúč `email`, nie `invitedEmail`
      const payload = {
        email: (data as any).invitedEmail,
        memberName: (data as any).memberName,
        role: (data as any).role,
        notes: (data as any).notes,
      };
      const res = await apiRequest("POST", "/api/team-members", payload);
      return await res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/team-members"] });
      form.reset();
      toast({
        title: "Člen tímu pozvaný",
        description: "Pozvánka bola úspešne odoslaná",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Chyba pri pozváni",
        description: error.message,
        variant: "default",
      });
    },
  });

  // Mutácia pre vytvorenie člena s účtom
  const createTeamMemberMutation = useMutation({
    mutationFn: async (data: CreateTeamMemberData) => {
      const res = await apiRequest("POST", "/api/team-members/create-with-user", data);
      return await res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/team-members"] });
      form.reset();
      toast({
        title: "Člen tímu vytvorený",
        description: "Člen tímu bol úspešne vytvorený s novým používateľským účtom",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Chyba pri vytváraní",
        description: error.message,
        variant: "default",
      });
    },
  });

  // Mutácia pre odstránenie člena tímu
  const removeTeamMemberMutation = useMutation({
    mutationFn: async (memberId: number) => {
      await apiRequest("DELETE", `/api/team-members/${memberId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/team-members"] });
      toast({
        title: "Člen tímu odstránený",
        description: "Člen tímu bol úspešne odstránený",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Chyba pri odstraňovaní",
        description: error.message,
        variant: "default",
      });
    },
  });

  // Mutácia pre aktualizáciu člena tímu
  const updateTeamMemberMutation = useMutation({
    mutationFn: async ({ memberId, memberName, role }: { memberId: number; memberName?: string; role?: string }) => {
      const data: any = {};
      if (memberName !== undefined) data.memberName = memberName;
      if (role !== undefined) data.role = role;
      const res = await apiRequest("PATCH", `/api/team-members/${memberId}`, data);
      return await res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/team-members"] });
      setEditingMemberId(null);
      setEditingMemberName("");
      setEditingMemberRole("");
      toast({
        title: "Člen tímu aktualizovaný",
        description: "Člen tímu bol úspešne aktualizovaný",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Chyba pri aktualizácii",
        description: error.message,
        variant: "default",
      });
    },
  });

  const resendInvitationMutation = useMutation({
    mutationFn: async (memberId: number) => {
      const res = await apiRequest("POST", `/api/team-members/${memberId}/resend-invitation`);
      return await res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/team-members"] });
      toast({
        title: "Pozvánka preposlaná",
        description: "Pozvánka bola úspešne znovu odoslaná",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Chyba pri preposlaní",
        description: error.message,
        variant: "default",
      });
    },
  });

  const handleEditMember = (member: TeamMember) => {
    setEditingMemberId(member.id);
    setEditingMemberName(member.memberName || "");
    setEditingMemberRole(member.role || "");
  };

  const handleSaveEdit = () => {
    if (!editingMemberId) return;
    updateTeamMemberMutation.mutate({
      memberId: editingMemberId,
      memberName: editingMemberName,
      role: editingMemberRole,
    });
  };

  // Mutácia pre resetovanie hesla člena tímu
  const resetPasswordMutation = useMutation({
    mutationFn: async ({ memberId, newPassword }: { memberId: number, newPassword: string }) => {
      await apiRequest("PATCH", `/api/team-members/${memberId}/reset-password`, { newPassword });
    },
    onSuccess: () => {
      setResetPasswordMemberId(null);
      resetPasswordForm.reset();
      toast({
        title: "Úspech",
        description: "Heslo bolo úspešne zresetované",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Informácia",
        description: error.message,
        variant: "default",
      });
    },
  });

  const onSubmit = (data: CreateTeamMemberData) => {
    if (createWithAccount) {
      // Backend očakáva `email`, nie `invitedEmail`
      createTeamMemberMutation.mutate({
        email: (data as any).invitedEmail,
        memberName: (data as any).memberName,
        password: (data as any).password,
        role: (data as any).role,
        notes: (data as any).notes,
      } as any);
    } else {
      // Mapuj na payload s kľúčom `email` pre backend
      inviteTeamMemberMutation.mutate({
        invitedEmail: (data as any).invitedEmail,
        memberName: (data as any).memberName,
        role: (data as any).role,
        notes: (data as any).notes,
      } as any);
    }
  };

  const onResetPasswordSubmit = (data: ResetPasswordData) => {
    if (resetPasswordMemberId) {
      resetPasswordMutation.mutate({
        memberId: resetPasswordMemberId,
        newPassword: data.newPassword
      });
    }
  };

  if (!user || !hasTeamAccess) {
    return (
      <div className="page-container">
        <Card>
          <CardHeader>
            <CardTitle>Prístup zamietnutý</CardTitle>
            <CardDescription>
              Na túto sekciu nemáte oprávnenie.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="section-spacing">
        <h1 className="page-title">Správa tímu</h1>
        <p className="text-muted-foreground text-lg">
          Spravujte členov vášho tímu a ich oprávnenia
        </p>
      </div>

      {/* Formulár pre pridanie nového člena */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <UserPlus className="h-5 w-5" />
            {createWithAccount ? "Vytvoriť nového člena tímu" : "Pozvať nového člena tímu"}
          </CardTitle>
          <CardDescription>
            {createWithAccount 
              ? "Vytvorte nového člena s používateľským účtom" 
              : "Pošlite pozvánku novému členovi do vášho tímu"
            }
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              {/* Prepínač medzi pozvaním a vytvorením účtu */}
              <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 p-3 sm:p-4 border rounded-lg">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input 
                    type="radio" 
                    name="memberType" 
                    checked={!createWithAccount}
                    onChange={() => setCreateWithAccount(false)}
                    className="text-primary"
                  />
                  <span>Poslať pozvánku emailom</span>
                </label>
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input 
                    type="radio" 
                    name="memberType" 
                    checked={createWithAccount}
                    onChange={() => setCreateWithAccount(true)}
                    className="text-primary"
                  />
                  <span>Vytvoriť účet priamo</span>
                </label>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="invitedEmail"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email</FormLabel>
                      <FormControl>
                        <Input placeholder="member@example.com" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="memberName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Meno člena</FormLabel>
                      <FormControl>
                        <Input placeholder="Janko Hraško" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="role"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Rola</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Vyberte rolu" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="employee">Zamestnanec</SelectItem>
                          <SelectItem value="admin">Administrátor</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {createWithAccount && (
                  <FormField
                    control={form.control}
                    name="password"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Heslo</FormLabel>
                        <FormControl>
                          <Input type="password" placeholder="Zadajte heslo..." {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}

                <div className="flex items-end">
                  <Button
                    type="submit"
                    disabled={inviteTeamMemberMutation.isPending || createTeamMemberMutation.isPending}
                    className="w-full"
                  >
                    {(inviteTeamMemberMutation.isPending || createTeamMemberMutation.isPending) && (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    )}
                    {createWithAccount ? "Vytvoriť člena" : "Poslať pozvánku"}
                  </Button>
                </div>
              </div>

              <FormField
                control={form.control}
                name="notes"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Poznámky (voliteľné)</FormLabel>
                    <FormControl>
                      <Input placeholder="Dodatočné poznámky o členovi..." {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </form>
          </Form>
        </CardContent>
      </Card>

      {/* Zoznam členov tímu */}
      <Card>
        <CardHeader>
          <CardTitle>Členovia tímu</CardTitle>
          <CardDescription>
            Spravujte existujúcich členov vášho tímu
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin" />
            </div>
          ) : teamMembers.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              Zatiaľ nemáte žiadnych členov tímu
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Meno</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Rola</TableHead>
                  <TableHead>Stav</TableHead>
                  <TableHead>Pozvaný</TableHead>
                  <TableHead>Akcie</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {Array.isArray(teamMembers) ? teamMembers.map((member) => (
                  <TableRow key={member.id}>
                    <TableCell>
                      {editingMemberId === member.id ? (
                        <Input
                          value={editingMemberName}
                          onChange={(e) => setEditingMemberName(e.target.value)}
                          className="w-32"
                        />
                      ) : (
                        <span onClick={() => handleEditMember(member)} className="cursor-pointer">
                          {member.memberName || "Bez mena"}
                        </span>
                      )}
                    </TableCell>
                    <TableCell>{member.invitedEmail}</TableCell>
                    <TableCell>
                      {editingMemberId === member.id ? (
                        <Select
                          value={editingMemberRole}
                          onValueChange={(value) => setEditingMemberRole(value)}
                        >
                          <SelectTrigger className="w-32">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="employee">Zamestnanec</SelectItem>
                            <SelectItem value="admin">Administrátor</SelectItem>
                          </SelectContent>
                        </Select>
                      ) : (
                        member.role === "employee" ? "Zamestnanec" : "Administrátor"
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={
                          member.status === "active"
                            ? "default"
                            : member.status === "pending"
                            ? "secondary"
                            : "destructive"
                        }
                      >
                        {member.status === "active"
                          ? "Aktívny"
                          : member.status === "pending"
                          ? "Čaká"
                          : "Neaktívny"}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {new Date(member.invitedAt).toLocaleDateString("sk-SK")}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        {editingMemberId === member.id ? (
                          <>
                            <Button
                              variant="default"
                              size="sm"
                              onClick={handleSaveEdit}
                              disabled={updateTeamMemberMutation.isPending}
                            >
                              Uložiť
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setEditingMemberId(null);
                                setEditingMemberName("");
                                setEditingMemberRole("");
                              }}
                              disabled={updateTeamMemberMutation.isPending}
                            >
                              Zrušiť
                            </Button>
                          </>
                        ) : (
                          <>
                            {/* Tlačidlo pre resetovanie hesla len pre členov s registrovaným účtom */}
                            {member.registeredUserId && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setResetPasswordMemberId(member.id)}
                                disabled={resetPasswordMutation.isPending}
                                title="Resetovať heslo"
                              >
                                <KeyRound className="h-4 w-4" />
                              </Button>
                            )}
                            {member.status === "pending" && !member.registeredUserId && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => resendInvitationMutation.mutate(member.id)}
                                disabled={resendInvitationMutation.isPending}
                                title="Preposlať pozvánku"
                              >
                                <Send className="h-4 w-4" />
                              </Button>
                            )}
                            <Button
                              variant="destructive"
                              size="sm"
                              onClick={() => removeTeamMemberMutation.mutate(member.id)}
                              disabled={removeTeamMemberMutation.isPending}
                              title="Odstrániť člena"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                )) : null}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Dialóg pre resetovanie hesla */}
      <Dialog open={resetPasswordMemberId !== null} onOpenChange={(open) => !open && setResetPasswordMemberId(null)}>
        <DialogContent className="bg-background/80 backdrop-blur-xl shadow-2xl border-l-4 border-l-primary/30">
          <DialogHeader>
            <DialogTitle>Resetovať heslo člena tímu</DialogTitle>
            <DialogDescription>
              Zadajte nové heslo pre člena tímu. Heslo musí obsahovať aspoň 8 znakov, veľké písmeno, číslo a špeciálny znak.
            </DialogDescription>
          </DialogHeader>
          <Form {...resetPasswordForm}>
            <form onSubmit={resetPasswordForm.handleSubmit(onResetPasswordSubmit)} className="space-y-4">
              <FormField
                control={resetPasswordForm.control}
                name="newPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Nové heslo</FormLabel>
                    <FormControl>
                      <Input
                        type="password"
                        placeholder="Zadajte nové heslo..."
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="flex justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setResetPasswordMemberId(null)}
                  disabled={resetPasswordMutation.isPending}
                >
                  Zrušiť
                </Button>
                <Button
                  type="submit"
                  disabled={resetPasswordMutation.isPending}
                >
                  {resetPasswordMutation.isPending && (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  )}
                  Resetovať heslo
                </Button>
              </div>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    </div>
  );
}